/**
 * Format bytes to human readable format
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string with appropriate unit (B, KB, MB, GB, TB)
 */
export function formatBytes(
  bytes: number | null | undefined,
  decimals = 1,
): string {
  if (bytes === null || bytes === undefined) {
    return '—';
  }

  if (bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const formattedValue = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

  return `${formattedValue} ${sizes[i]}`;
}

/**
 * Format a date to relative time (e.g., "2 hours ago", "yesterday")
 * @param date - Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - targetDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffMs < 60000) {
    // Less than 1 minute
    return 'just now';
  } else if (diffMinutes < 60) {
    // Less than 1 hour
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  } else if (diffHours < 24) {
    // Less than 1 day
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  } else if (diffDays === 1) {
    return 'yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return targetDate.toLocaleDateString();
  }
}

/**
 * Format a number with thousands separators
 * @param num - Number to format
 * @returns Formatted string with comma separators
 */
export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) {
    return '—';
  }
  return num.toLocaleString();
}

export function formatSemanticVersion(version: number | string | null): string {
  if (version === null) {
    return '—';
  }
  return `v${version.toString()}`;
}
